
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import ArticleForm from "./ArticleForm";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface AIArticleFormProps {
  setIsLoading: (isLoading: boolean) => void;
}

interface GeneratedArticle {
  title: string;
  description: string;
  keywords: string;
  content: string;
}

const AIArticleForm = ({ setIsLoading }: AIArticleFormProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedArticle, setGeneratedArticle] = useState<GeneratedArticle | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleGenerateArticle = async () => {
    if (!prompt.trim()) {
      toast({
        title: "Error",
        description: "Please enter a prompt to generate an article",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    setIsLoading(true);
    setError(null);

    try {
      console.log("Sending prompt to generate article:", prompt.trim());
      
      const response = await supabase.functions.invoke("generate-article-content", {
        body: { prompt: prompt.trim() }
      });
      
      console.log("Response from generate-article-content:", response);
      
      if (response.error) {
        console.error("Error from supabase function:", response.error);
        throw new Error(response.error.message || "Failed to generate article content");
      }

      if (!response.data) {
        throw new Error("No data returned from article generation");
      }

      const data = response.data;
      console.log("Generated article data:", data);

      // Handle case where the response contains an error message
      if (data.error) {
        throw new Error(data.error);
      }

      if (!data.title || !data.content) {
        throw new Error("Failed to generate complete article content");
      }

      setGeneratedArticle({
        title: data.title,
        description: data.description || "",
        keywords: data.keywords || "",
        content: data.content
      });

      toast({
        title: "Success",
        description: "Article content generated successfully",
      });
    } catch (error) {
      console.error("Error generating article:", error);
      setError(error instanceof Error ? error.message : "Failed to generate article content");
      toast({
        title: "Error",
        description: "Failed to generate article content",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setGeneratedArticle(null);
    setPrompt("");
    setError(null);
  };

  if (generatedArticle) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between">
          <h2 className="text-xl font-semibold">Complete and Publish Your AI-Generated Article</h2>
          <Button variant="outline" onClick={handleReset}>
            Generate New Article
          </Button>
        </div>
        
        <ArticleForm 
          onSuccess={() => {
            toast({
              title: "Success",
              description: "AI-generated article saved successfully",
            });
            navigate("/admin/articles/list");
          }}
          initialValues={generatedArticle}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-amber-800 mb-2">AI Article Generation Tips</h3>
        <ul className="list-disc ml-5 text-amber-700 space-y-1 text-sm">
          <li>Be specific about the potato farming topic you want to cover</li>
          <li>Include key points or aspects you want the article to address</li>
          <li>Mention any specific Scottish context that's important</li>
          <li>Specify the target audience (farmers, consumers, industry experts)</li>
          <li>After generation, you can edit any part of the article before publishing</li>
        </ul>
      </div>

      <div>
        <label htmlFor="prompt" className="block text-sm font-medium mb-2">
          What would you like to write about?
        </label>
        <Textarea
          id="prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="E.g., Write an article about sustainable potato farming practices in Scotland, including water conservation methods and organic pest control. Target audience is Scottish potato farmers."
          className="h-40"
          required
        />
      </div>

      <Button
        onClick={handleGenerateArticle}
        disabled={isGenerating || !prompt.trim()}
        className="w-full"
      >
        {isGenerating ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Generating Article...
          </>
        ) : (
          "Generate Article Content"
        )}
      </Button>
    </div>
  );
};

export default AIArticleForm;
